import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { testimonials } from '@/mocks/testimonials';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '免费资源导航',
    url: 'https://www.free-site-nav.com',
    description: '免费网站资源导航平台，精选全网最优质的免费建站、免费域名、免费主机等实用资源。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.free-site-nav.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 免费资源导航',
    description: '了解免费资源导航的品牌故事、核心价值观和联系方式。致力于为个人和企业提供最优质的免费网站资源导航服务。',
    url: 'https://www.free-site-nav.com/about',
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt')?.toString().trim() || '';
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && value.toString().trim()) {
          body.append(key, value.toString().trim());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9sler58o1vn02ekuhdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 免费资源导航"
        description="了解免费资源导航的品牌故事、核心价值观和联系方式。致力于为个人和企业提供最优质的免费网站资源导航服务。"
        keywords="关于我们,免费资源导航,品牌介绍,联系方式"
        canonicalUrl="https://www.free-site-nav.com/about"
        ogTitle="关于我们 - 免费资源导航"
        ogDescription="了解免费资源导航的品牌故事、核心价值观和联系方式。"
        ogUrl="https://www.free-site-nav.com/about"
        structuredData={{ ...orgSchema, ...breadcrumbSchema, ...webPageSchema }}
      />

      <Navbar />
      <ScrollToTop />

      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        {/* Brand Intro Hero */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">关于免费资源导航</h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  免费资源导航是一个致力于为个人开发者、创业者和小型企业提供免费网站资源的一站式导航平台。我们深知，在互联网创业的起步阶段，每一分预算都很宝贵。因此，我们精心筛选和整理了全网最优质的免费建站平台、免费域名、免费主机空间、免费CDN、免费SSL证书等实用资源，帮助你在零预算的条件下搭建出专业水准的网站。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  我们的团队由一群热爱互联网的产品经理、开发者和设计师组成，每个人都亲身体验过预算有限但梦想无限的创业阶段。正是这份共同的经历，驱使我们打造了这个平台——让每一个有想法的人，都能以零成本开始自己的互联网之旅。
                </p>
              </div>
              <div className="flex-shrink-0 w-full lg:w-80">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20modern%20team%20collaboration%20and%20resource%20sharing%20concept%2C%20warm%20golden%20and%20cream%20tones%2C%20minimalist%20geometric%20design%2C%20clean%20professional%20aesthetic%2C%20soft%20diffused%20lighting%2C%20creative%20atmosphere&width=640&height=640&seq=about-hero-01&orientation=squarish"
                  alt="免费资源导航团队"
                  title="免费资源导航 - 关于我们"
                  className="w-full rounded-lg object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Values */}
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-10">我们的使命与价值观</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'ri-heart-line', title: '让建站零门槛', desc: '我们相信，每个人都应该拥有在互联网上展示自己的权利。无论你的预算、技术背景如何，都能在这里找到适合的免费建站方案。', color: 'accent' },
                { icon: 'ri-shield-star-line', title: '精选优质资源', desc: '我们不追求数量，而是精心筛选每一个推荐资源。每个上线的资源都经过团队的实际测试和评估，确保质量可靠。', color: 'primary' },
                { icon: 'ri-user-heart-line', title: '用户价值优先', desc: '用户的信任是我们最宝贵的资产。我们坚持客观公正的推荐，绝不因为商业合作影响资源的评价和排序。', color: 'secondary' },
              ].map((item) => {
                const bgMap: Record<string, string> = { accent: 'bg-accent-500', primary: 'bg-primary-500', secondary: 'bg-secondary-500' };
                const textMap: Record<string, string> = { accent: 'text-accent-700', primary: 'text-primary-700', secondary: 'text-secondary-700' };
                const badgeMap: Record<string, string> = { accent: 'bg-accent-100', primary: 'bg-primary-100', secondary: 'bg-secondary-100' };
                return (
                  <div key={item.title} className="bg-background-50 rounded-lg p-6 text-center">
                    <div className={`w-12 h-12 rounded-lg ${bgMap[item.color]} flex items-center justify-center mx-auto mb-4`}>
                      <i className={`${item.icon} text-background-50 text-xl`}></i>
                    </div>
                    <h3 className={`text-base font-semibold font-heading mb-2 ${textMap[item.color]}`}>{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-3">用户口碑</h2>
            <p className="text-sm text-foreground-500 text-center mb-10">听听我们的用户怎么说</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="flex items-center gap-0.5 mt-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-400 text-sm"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-3">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">有任何问题或建议？欢迎随时与我们取得联系</p>

            <form onSubmit={handleSubmit} data-readdy-form className="space-y-5" noValidate>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的邮箱地址"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入你的消息（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              {formStatus === 'error' && formError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                  {formError}
                </div>
              )}

              {formStatus === 'success' && (
                <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                  感谢你的留言！我们会尽快回复。
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium cursor-pointer transition-all duration-300 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {formStatus === 'submitting' ? '提交中...' : '发送消息'}
              </button>
            </form>
          </div>
        </section>

        {/* Quick Contact Info */}
        <section className="w-full bg-background-50 py-10">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
              <div>
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <p className="text-sm text-foreground-500">contact@free-site-nav.com</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-accent-600"></i>
                </div>
                <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
              </div>
              <div>
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-question-answer-line text-secondary-600"></i>
                </div>
                <p className="text-sm text-foreground-500">通常24小时内回复</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}